/**
 * CRUD Tool Generator — detects CRUD patterns in action flows
 * (restApi with POST/PUT/DELETE methods) and generates typed tools.
 */

import type { CogoFlow, CogoFlowStep, CogoBasicStep, CogoBlockDef } from "@cogo/types"
import type { WebMCPTool, JsonSchema } from "../core/tool-registry"
import type { GeneratorRuntime } from "./form-tools"

// ── Types ──────────────────────────────────────────────────

interface CrudOperation {
  flowName: string
  method: "POST" | "PUT" | "DELETE" | "GET"
  url: string
  crudType: "create" | "update" | "delete" | "read"
  entity: string
  bodyFields: string[]
  eventParams: string[]
}

// ── Detection ──────────────────────────────────────────────

/** Extract CRUD operations from flows by scanning for restApi steps */
function detectCrudOperations(flows: Record<string, CogoFlow>): CrudOperation[] {
  const operations: CrudOperation[] = []

  for (const [flowName, flow] of Object.entries(flows)) {
    // Skip init flows — they're for loading, not CRUD
    if (flow.trigger === "init") continue

    const restApiSteps = findRestApiSteps(flow.steps)
    for (const step of restApiSteps) {
      const params = step.params as Record<string, unknown>
      const method = (params.method as string)?.toUpperCase()
      const url = params.url as string

      if (!method || !url) continue

      // Determine CRUD type
      let crudType: CrudOperation["crudType"] | null = null
      if (method === "POST") crudType = "create"
      else if (method === "PUT") {
        // Distinguish create vs update by examining body or flow name
        if (/create|add|new/i.test(flowName)) crudType = "create"
        else if (/delete|remove/i.test(flowName)) crudType = "delete"
        else crudType = "update"
        // Also check body for action markers (Cogo convention: body.itemList[0].action = "c"/"u"/"d")
        const body = params.body as Record<string, unknown> | undefined
        if (body) {
          const actionValue = findActionMarker(body)
          if (actionValue === "c") crudType = "create"
          else if (actionValue === "u") crudType = "update"
          else if (actionValue === "d") crudType = "delete"
        }
      } else if (method === "DELETE") crudType = "delete"

      if (!crudType) continue

      // Infer entity name from URL or flow name
      const entity = inferEntityName(url, flowName)

      // Extract body fields for create/update
      const bodyFields = crudType !== "delete"
        ? extractBodyFields(params.body)
        : []

      // Extract event params
      const eventParams = extractFlowEventParams(flow.steps)

      operations.push({
        flowName,
        method: method as CrudOperation["method"],
        url,
        crudType,
        entity,
        bodyFields,
        eventParams,
      })
    }
  }

  // Deduplicate — same flow might have multiple restApi calls (conditional paths)
  // Keep one per flowName, prefer the first occurrence
  const seen = new Set<string>()
  return operations.filter((op) => {
    const key = `${op.flowName}:${op.crudType}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function findRestApiSteps(steps: unknown): CogoBasicStep[] {
  const result: CogoBasicStep[] = []

  function walk(steps: unknown): void {
    if (!Array.isArray(steps)) return
    for (const rawStep of steps) {
      if (!rawStep || typeof rawStep !== "object") continue
      const step = rawStep as CogoFlowStep
      if (step.type === "basic" && step.action === "restApi") {
        result.push(step as CogoBasicStep)
      }
      if (step.type === "conditional") {
        walk(step.then)
        if (step.else) walk(step.else)
      }
      if (step.type === "loop" || step.type === "parallel") {
        walk(step.steps)
      }
      if (step.type === "tryCatch") {
        // CRUD tools invoke the whole flow, so failure/cleanup branches cannot
        // be advertised as independently callable operations.
        walk(step.try)
      }
      if (step.type === "switch") {
        for (const caseSteps of Object.values(step.cases)) {
          walk(caseSteps)
        }
        if (step.default) walk(step.default)
      }
      if (step.type === "stage") {
        walk(step.onEnter)
        walk(step.steps)
        walk(step.onSuccess)
      }
      if (step.type === "saga") walk(step.stages)
    }
  }

  walk(steps)
  return result
}

function findActionMarker(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined
  const obj = body as Record<string, unknown>

  // Check direct action field
  if (typeof obj.action === "string") return obj.action

  // Check arrays (common pattern: { itemList: [{ action: "c", ... }] })
  for (const value of Object.values(obj)) {
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0]
      if (first && typeof first === "object" && typeof (first as Record<string, unknown>).action === "string") {
        return (first as Record<string, unknown>).action as string
      }
    }
  }

  return undefined
}

function inferEntityName(url: string, flowName: string): string {
  // Try to extract entity from URL path: /commerce-api/items → items
  const urlMatch = /\/([a-zA-Z-]+?)(?:\/|\?|$)/.exec(url.replace(/^\/[a-z]+-api\//, "/"))
  if (urlMatch) {
    return urlMatch[1].replace(/-/g, "_").replace(/s$/, "")  // plurals → singular
  }

  // Fall back to flow name
  return flowName
    .replace(/^(validate|submit|save|create|update|delete|remove)And?/i, "")
    .replace(/^(create|update|delete|save|submit)_?/i, "")
    .replace(/^_/, "")
    || flowName
}

function extractBodyFields(body: unknown): string[] {
  if (!body || typeof body !== "object") return []
  const fields: string[] = []

  function walk(obj: unknown, prefix: string): void {
    if (!obj || typeof obj !== "object") return
    if (Array.isArray(obj)) {
      // Look at first item for template fields
      if (obj.length > 0) walk(obj[0], prefix)
      return
    }
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (key === "action") continue // Skip Cogo action markers
      if (typeof value === "string" && value.startsWith("#{")) {
        fields.push(key)
      } else if (typeof value === "object" && value !== null) {
        walk(value, `${prefix}${key}.`)
      }
    }
  }

  walk(body, "")
  return fields
}

function extractFlowEventParams(steps: unknown): string[] {
  const params = new Set<string>()
  const re = /#\{event\.(\w+)\}/g

  function scanValue(v: unknown): void {
    if (typeof v === "string") {
      let m: RegExpExecArray | null
      re.lastIndex = 0
      while ((m = re.exec(v)) !== null) params.add(m[1])
    } else if (Array.isArray(v)) {
      for (const item of v) scanValue(item)
    } else if (v && typeof v === "object") {
      for (const val of Object.values(v)) scanValue(val)
    }
  }

  function walk(steps: unknown): void {
    if (!Array.isArray(steps)) return
    for (const rawStep of steps) {
      if (!rawStep || typeof rawStep !== "object") continue
      const step = rawStep as CogoFlowStep
      if (step.type === "basic" || step.type === "expression") {
        scanValue((step as CogoBasicStep).params)
      }
      if (step.type === "conditional") {
        scanValue(step.condition)
        walk(step.then)
        if (step.else) walk(step.else)
      }
      if (step.type === "loop") walk(step.steps)
      if (step.type === "parallel") walk(step.steps)
      if (step.type === "tryCatch") {
        walk(step.try)
        walk(step.catch)
        walk(step.finally)
      }
      if (step.type === "switch") {
        scanValue(step.value)
        for (const cs of Object.values(step.cases)) walk(cs)
        if (step.default) walk(step.default)
      }
      if (step.type === "stage") {
        walk(step.onEnter)
        walk(step.steps)
        walk(step.onSuccess)
        walk(step.onError)
        walk(step.onFinally)
        walk(step.compensate)
      }
      if (step.type === "saga") walk(step.stages)
    }
  }

  walk(steps)
  return Array.from(params)
}

function sanitizeToolName(raw: string): string {
  return raw
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .toLowerCase()
}

// ── Generator ──────────────────────────────────────────────

export function generateCrudTools(
  block: CogoBlockDef,
  flows: Record<string, CogoFlow>,
): WebMCPTool[] {
  const operations = detectCrudOperations(flows)
  const tools: WebMCPTool[] = []

  for (const op of operations) {
    const entityClean = sanitizeToolName(op.entity)
    const toolName = `${op.crudType}_${entityClean}`

    const properties: Record<string, JsonSchema> = {}
    const required: string[] = []

    if (op.crudType === "delete") {
      // Delete tools just need an ID
      for (const param of op.eventParams) {
        properties[param] = { type: "string", description: `${param} of the ${op.entity} to delete` }
        required.push(param)
      }
    } else {
      // Create/Update tools need body fields
      for (const field of op.bodyFields) {
        properties[field] = { type: "string", description: `${field} of the ${op.entity}` }
      }
      // Also include event params not covered by body fields
      for (const param of op.eventParams) {
        if (!properties[param]) {
          properties[param] = { type: "string", description: `${param}` }
        }
      }
    }

    tools.push({
      name: toolName,
      description: flows[op.flowName]?.narrative?.effect ?? `${op.crudType.charAt(0).toUpperCase() + op.crudType.slice(1)} ${op.entity} via ${op.method} ${op.url.split("?")[0]}`,
      inputSchema: {
        type: "object",
        properties,
        ...(required.length > 0 ? { required } : {}),
      },
      handler: createCrudHandler(op.flowName),
      metadata: {
        blockName: block.blockType,
        readOnly: op.crudType === "read",
        tags: ["crud", op.crudType],
      },
    })
  }

  return tools
}

function createCrudHandler(
  flowName: string,
): (params: Record<string, unknown>) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      return runtime.actionEngine.executeFlow(flowName, params)
    }
  }
}
